import {
  IMAGE_ASPECT_RATIOS,
  IMAGE_FOCAL_POINTS,
  IMAGE_MOBILE_BEHAVIOURS,
  IMAGE_RADII,
  IMAGE_SIZES,
  lookup,
} from '../fields/tokens';

export type ImgixSizing = { kind: 'fixed'; widthPx: number } | { kind: 'fluid'; candidateWidthsPx: readonly number[] };

/** Raw prop shape {@link resolveImageStyle} consumes -- exactly the subset of `ImageProps` that
 * feeds visual CSS resolution. Preset keys stay plain strings and the custom companion stays
 * unknown because the function's purpose is to tolerate unvalidated persisted data. */
export type ImageStyleInput = {
  width: string;
  customWidth?: unknown;
  aspectRatio: string;
  objectFit: string;
  focalPoint: string;
  borderRadius: string;
  mobile: string;
};

/** Every visual value `render` needs, already resolved to a literal CSS value (or `undefined`
 * where a custom property should be left unset so the stylesheet's own fallback engages). */
export type ResolvedImageStyle = {
  width: string;
  aspectRatio: string;
  objectFit: string;
  focalPoint: string;
  borderRadius: string;
  /** Set only when `mobile` resolves to 'full' -- 'keep' leaves this `undefined` so the
   * stylesheet's own fallback chain (`var(--cms-image-mobile-width, var(--cms-image-width, ...))`)
   * resolves to the desktop width instead, mirroring spacer.tsx's mobileHeight 'same' behaviour. */
  mobileWidth: string | undefined;
  /** True when `mobile` resolves to 'hide' -- callers decide WHERE to apply the resulting
   * `display: none` (the image, or the whole figure, depending on whether there's a caption to
   * hide alongside it) and whether to suppress it entirely in the editor. */
  mobileHide: boolean;
};

/**
 * Pure resolver for every prop->CSS decision the Image block's `render` needs, extracted so each
 * individually-simple rule -- kept-key literal passthrough, snap-to-default for new keys, and the
 * circle/aspect-ratio cross-field override -- can be read and unit-tested as a flat table of
 * inputs and outputs, rather than traced through JSX-building code.
 *
 * Two different fallback strategies apply to different keys, matching `tokens.ts`'s `lookup()`
 * doc comment:
 *
 * - KEPT keys (`width`, `objectFit`, `borderRadius`) use literal passthrough: an unrecognised
 *   value is a legacy pre-preset value (e.g. a raw `'320px'` width saved before size presets
 *   existed) and must render EXACTLY as authored, not snap to a default.
 * - NEW keys (`customWidth`, `aspectRatio`, `focalPoint`, `mobile`) use snap-to-default: these keys did not
 *   exist before this PR, so an unrecognised value can only be a data error (hand-edited/corrupted
 *   JSON), and falling back to a safe default is correct.
 *
 * `borderRadius === 'circle'` additionally overrides `aspectRatio` to a square regardless of what
 * was chosen there -- an ellipse is not a circle. This checks the exact preset KEY, not the
 * resolved radius value, so a legacy raw `borderRadius` like `'8px'` (kept-key literal passthrough)
 * does NOT trigger it -- only the current 'Circle' preset does.
 */
export function resolveImageStyle({
  width,
  customWidth,
  aspectRatio,
  objectFit,
  focalPoint,
  borderRadius,
  mobile,
}: ImageStyleInput): ResolvedImageStyle {
  // Literal passthrough for every KEPT key. Custom is the one explicit exception: its companion
  // value crosses an unvalidated JSON boundary, so only a bounded whole-pixel width reaches CSS.
  const resolvedWidth =
    width === 'custom' ? `${resolveCustomImageWidth(customWidth)}px` : lookup(IMAGE_SIZES, width, width);
  const resolvedBorderRadius = lookup(IMAGE_RADII, borderRadius, borderRadius);
  const resolvedObjectFit = objectFit;

  // Snap-to-default for NEW keys.
  const resolvedFocalPoint = lookup(IMAGE_FOCAL_POINTS, focalPoint, IMAGE_FOCAL_POINTS.center);
  const resolvedMobile = lookup(IMAGE_MOBILE_BEHAVIOURS, mobile, IMAGE_MOBILE_BEHAVIOURS.full);

  // CROSS-FIELD COUPLING, not a bug: see this function's doc comment above.
  const isCircle = borderRadius === 'circle';
  const resolvedAspectRatio = isCircle
    ? IMAGE_ASPECT_RATIOS.square
    : lookup(IMAGE_ASPECT_RATIOS, aspectRatio, IMAGE_ASPECT_RATIOS.original);

  return {
    width: resolvedWidth,
    aspectRatio: resolvedAspectRatio,
    objectFit: resolvedObjectFit,
    focalPoint: resolvedFocalPoint,
    borderRadius: resolvedBorderRadius,
    mobileWidth: resolvedMobile === 'full' ? IMAGE_SIZES.full : undefined,
    mobileHide: resolvedMobile === 'hide',
  };
}

/**
 * Candidate widths (CSS pixels) for fluid `w`-descriptor srcSets. Covers the full range from
 * typical mobile viewports to high-DPI wide monitors, so a `full`-width image always has a
 * candidate that closely matches the actual rendered slot.
 *
 * The top candidate (3840) covers 2× DPR at a 1920 px viewport — the most common "Retina desktop"
 * case. Without it the browser would pick 3200 px and upscale ~20%. 4096 was considered but
 * omitted: the marginal quality gain (≈7%) is not worth the payload increase for the small slice
 * of clients with 2× DPR at viewports wider than 1920 px, and Imgix server-clamps at 8192 anyway.
 * Trade-off note: more candidates means a larger `srcSet` attribute and a larger top-candidate
 * payload; 3840 is judged the right stopping point given the DPR/viewport distribution.
 */
const FLUID_CANDIDATE_WIDTHS_PX = [480, 768, 1024, 1366, 1600, 1920, 2560, 3200, 3840] as const;
export const MAX_IMGIX_OUTPUT_WIDTH_PX = 8192;

/**
 * Maximum pixel width for an explicit `kind:'fixed'` sizing. Clamps absurd stored values
 * (e.g. `'99999px'`) to the top of the fluid candidate ladder so a fixed-width request never
 * asks Imgix for more pixels than the fluid path would. Derived from the ladder so the two
 * stay in sync automatically when the ladder changes.
 */
export const MAX_FIXED_WIDTH_PX = FLUID_CANDIDATE_WIDTHS_PX[FLUID_CANDIDATE_WIDTHS_PX.length - 1];
export const MIN_FIXED_WIDTH_PX = 1;

/**
 * Default for an absent or malformed Custom width. At half the existing Small preset (240px),
 * 120px is a useful logo-sized starting point while keeping Small and every other preset unchanged.
 */
export const DEFAULT_CUSTOM_IMAGE_WIDTH_PX = 120;

/**
 * Accepts only numeric whole pixels. Valid integers are clamped to the supported delivery range;
 * every other runtime shape falls back to the 120px logo-sized default. This is the single boundary
 * used by both CSS rendering and Imgix delivery, so hostile persisted JSON cannot make them drift.
 */
export function resolveCustomImageWidth(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) return DEFAULT_CUSTOM_IMAGE_WIDTH_PX;
  return Math.min(MAX_FIXED_WIDTH_PX, Math.max(MIN_FIXED_WIDTH_PX, value));
}

/**
 * Per-preset target pixel widths, kept in one table rather than scattered as literals.
 * `fit` is 1600: `fit-content` resolves the intrinsic width to the delivered image width, so
 * the delivered width must be large enough to fill any reasonable container without upscaling.
 * `full` is fluid: a full-width slot is viewport-wide and needs `w`-descriptor + `sizes` so
 * the browser can pick the right candidate for its actual viewport.
 */
const PRESET_SIZING: Record<string, ImgixSizing> = {
  fit: { kind: 'fixed', widthPx: 1600 },
  small: { kind: 'fixed', widthPx: 240 },
  medium: { kind: 'fixed', widthPx: 480 },
  large: { kind: 'fixed', widthPx: 800 },
  full: { kind: 'fluid', candidateWidthsPx: FLUID_CANDIDATE_WIDTHS_PX },
};

/**
 * Resolves a `width` prop (either an `IMAGE_SIZES` preset key or a legacy raw CSS value) to an
 * {@link ImgixSizing} descriptor that governs how `buildImgixDelivery` (in `image-src.ts`)
 * delivers the image.
 *
 * - Fixed presets (`small`, `medium`, `large`, `fit`) → `kind: 'fixed'` with `x` descriptors.
 *   `fit` uses 1600px so `fit-content` layout resolves to the full delivered width for any
 *   container under 1600px, rather than the old 480px which would shrink the image visually.
 * - `full` → `kind: 'fluid'` with `w` descriptors + `sizes: '100vw'`, so the browser selects a
 *   candidate matching the actual viewport width rather than always fetching the 1x candidate.
 * - Custom → `kind: 'fixed'` at its validated whole-pixel width, preserving the approved DPR
 *   `x`-descriptor contract without a `sizes` attribute.
 * - A legacy raw pixel value (`'320px'`) → `kind: 'fixed'` with that pixel count.
 * - Any other legacy raw shape (percentage, `auto`, unparsable) → `kind: 'fluid'`, because a
 *   percentage slot is elastic and needs the browser to pick based on the real rendered width.
 */
export function imgixSizing(width: string, customWidth?: unknown): ImgixSizing {
  if (width === 'custom') {
    return { kind: 'fixed', widthPx: resolveCustomImageWidth(customWidth) };
  }
  if (Object.prototype.hasOwnProperty.call(PRESET_SIZING, width)) {
    return PRESET_SIZING[width];
  }
  const pxMatch = /^(\d+)px$/.exec(width);
  if (pxMatch) {
    const widthPx = Number(pxMatch[1]);
    // Clamp to the fluid ladder top: an absurd stored value like '99999px' would otherwise fire a
    // request for a 99999 px image. Imgix server-clamps at 8192 anyway, but it defeats the
    // optimisation and wastes bandwidth. A non-positive value falls through to fluid (see below).
    if (widthPx > 0) return { kind: 'fixed', widthPx: Math.min(widthPx, MAX_FIXED_WIDTH_PX) };
    // '0px' is not a valid Imgix target — fall through to fluid like any other unrecognised value.
  }
  // A percentage or other elastic value: treat as fluid so the browser picks a candidate based
  // on the actual rendered slot rather than a fixed number that could exceed it.
  return { kind: 'fluid', candidateWidthsPx: FLUID_CANDIDATE_WIDTHS_PX };
}
